import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Button, Spinner } from 'design-system/react';

import { listSimulations, createSimulation } from '../../client';
import { useDocumentTitle } from '../../hooks';
import { SimulationStateBadge } from '../atoms';
import { Page } from '../organisms';

export function OperatorSimulationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');

  useDocumentTitle('Simulations');

  const { data: simulations, isLoading } = useQuery({
    queryKey: ['simulations'],
    queryFn: () => listSimulations({ throwOnError: true }).then(({ data }) => data),
    refetchInterval: 10_000,
  });

  const createSimulationMutation = useMutation({
    mutationFn: (newLabel: string) =>
      createSimulation({
        body: { label: newLabel || undefined },
        throwOnError: true,
      }).then(({ data }) => data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['simulations'] });
      navigate(`/operator/${created.id}`);
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createSimulationMutation.mutate(label.trim());
  };

  return (
    <Page title="Simulations" hideBackButton>
      {/* Create simulation */}
      <form onSubmit={handleCreate} className="flex items-center gap-2 w-full max-w-lg mx-auto mt-4 mb-6">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Simulation label (optional)"
          className="flex-1 px-4 py-3 rounded-lg border border-border-default bg-bg-elevated text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-action-primary"
        />
        <Button variant="primary" type="submit" disabled={createSimulationMutation.isPending}>
          {createSimulationMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </form>

      {createSimulationMutation.isError && (
        <p className="text-feedback-error text-sm text-center mb-4">Failed to create simulation. Please try again.</p>
      )}

      {/* Simulation list */}
      {isLoading ? (
        <div className="flex justify-center mt-8">
          <Spinner size="xl" />
        </div>
      ) : simulations && simulations.length > 0 ? (
        <div className="flex flex-col gap-2 w-full max-w-lg mx-auto">
          {simulations.map((sim) => (
            <button
              key={sim.id}
              onClick={() => navigate(`/operator/${sim.id}`)}
              className="flex items-center justify-between w-full p-4 rounded-lg border border-border-default bg-bg-elevated hover:bg-bg-subtle transition-colors text-left"
            >
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-sm font-medium text-text-primary truncate">{sim.label}</span>
                <span className="text-xs text-text-muted">
                  {new Date(sim.created_at).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <SimulationStateBadge state={sim.state} />
            </button>
          ))}
        </div>
      ) : (
        <p className="text-text-muted text-center mt-8">No simulations yet. Create one above to get started.</p>
      )}
    </Page>
  );
}
